'use client';

import { getAnalyticsRefPage } from '@modules/analytics/page-ref';
import {
  UTM_PARAM_KEYS,
  type UtmParams,
  isUtmParamKey,
  resolveUtmParams,
} from '@modules/analytics/utm-params';
import { usePathname, useSearchParams } from 'next/navigation';
import type React from 'react';
import { createContext, useContext, useEffect } from 'react';

import { analytics } from './analytics';
import type { TypedAnalytics } from './types';
import type { AnalyticsPagePropertiesInput, AnalyticsPlatforms } from '.cache/__types__';

const platforms = [
  'android',
  'api',
  'card_worker',
  'dash',
  'firebase_functions',
  'ios',
  'webflow',
  'intercom',
];

const trackedParamsList = ['ref_original', 'ref_page', 'ref_path', 'ref_cta'] as const;

type TrackedParamKey = (typeof trackedParamsList)[number];

function isTrackedParamKey(key: string): key is TrackedParamKey {
  return (trackedParamsList as readonly string[]).includes(key);
}

function isPlatform(candidate: unknown): candidate is AnalyticsPlatforms {
  return typeof candidate === 'string' && platforms.includes(candidate);
}

// biome-ignore lint/style/noNonNullAssertion: safe
const AnalyticsContext = createContext<TypedAnalytics>(null!);

export const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
  return <AnalyticsContext.Provider value={analytics}>{children}</AnalyticsContext.Provider>;
};

export function AnalyticsPageEvents() {
  const context = useContext(AnalyticsContext);

  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const pagePath = pathname ?? '';
    const properties: AnalyticsPagePropertiesInput = {
      path: pagePath,
      ref_page: getAnalyticsRefPage({ pathname: pagePath, searchParams }),
    };

    const urlUtmParams: UtmParams = {};
    searchParams?.forEach((value, key) => {
      if (isUtmParamKey(key)) {
        if (value) urlUtmParams[key] = value;
      } else if (isTrackedParamKey(key)) {
        properties[key] = value;
      } else if (key === 'gclid' || key === 'fbclid') {
        properties[key] = value;
        if (value) localStorage.setItem(key, value);
      } else if (key === 'ref_platform' && isPlatform(value)) {
        properties[key] = value;
      }
    });

    // UTMs survive the in-dash navigations that drop the query string (login →
    // signup → onboarding steps) instead of only appearing on the landing page view.
    const utmParams = resolveUtmParams(urlUtmParams);
    for (const key of UTM_PARAM_KEYS) {
      const value = utmParams[key];
      if (value) {
        properties[key] = value;
      }
    }

    context.page({ properties });
  }, [pathname, searchParams, context.page]);

  return <> </>;
}

export function useAnalytics() {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
}
